import type { IPriceProductDraft, IPriceProductDraftPreview } from '../types';
import type { VariantProductVariant } from '~/types/graphql';
import type { Variant } from '~/modules/campaign/types';

import {
  PRICE_PRODUCT_FIELD,
  PRICE_PRODUCT_FIELDS,
  PRICE_PRODUCT_PRODUCT_VARIANT_KEY,
} from '../constants';

const toDraftAmount = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value);

/**
 * The amounts as the fields show them, keyed by Control variant baseID. Read as held rather than
 * normalised, so a half-typed `10.` survives the keystroke that wrote it.
 */
export const toPriceProductDrafts = (
  productVariants: Variant['productVariants'],
): Record<string, IPriceProductDraft> =>
  (productVariants ?? []).reduce<Record<string, IPriceProductDraft>>((drafts, productVariant) => {
    if (!productVariant?.baseID) return drafts;

    drafts[productVariant.baseID] = {
      [PRICE_PRODUCT_FIELD.PRICE]: toDraftAmount(productVariant.price),
      [PRICE_PRODUCT_FIELD.COMPARE_PRICE]: toDraftAmount(productVariant.compareAtPrice),
    };

    return drafts;
  }, {});

/** A blank is held as null rather than `''` — the same as a saved record with no override. */
interface IToMergedProductVariantParams {
  productVariant: VariantProductVariant;
  draft: Partial<IPriceProductDraft>;
}

const toMergedProductVariant = ({
  productVariant,
  draft,
}: IToMergedProductVariantParams): VariantProductVariant =>
  PRICE_PRODUCT_FIELDS.reduce((merged, field) => {
    const value = draft[field];
    if (value === undefined) return merged;

    return { ...merged, [PRICE_PRODUCT_PRODUCT_VARIANT_KEY[field]]: value || null };
  }, productVariant);

export interface IMergePriceProductDraftsParams {
  productVariants: Variant['productVariants'];
  /** The challenger the amounts belong to — what a new entry is filed under. */
  variantID: string;
  drafts: IPriceProductDraftPreview;
}

/**
 * Writes typed amounts into the challenger's `productVariants`, adding an entry for a variant that has
 * none yet. This is the campaign the save bar diffs and discard restores, so the amounts need no
 * dirty/save/discard handling of their own.
 */
export const mergePriceProductDrafts = ({
  productVariants,
  variantID,
  drafts,
}: IMergePriceProductDraftsParams): VariantProductVariant[] => {
  const current = (productVariants ?? []).filter(
    (productVariant): productVariant is VariantProductVariant => Boolean(productVariant),
  );
  const existingBaseIDs = new Set(current.map((productVariant) => productVariant.baseID));

  const updated = current.map((productVariant) => {
    const draft = productVariant.baseID ? drafts[productVariant.baseID] : undefined;

    return draft ? toMergedProductVariant({ productVariant, draft }) : productVariant;
  });
  const added = Object.entries(drafts)
    .filter(([baseID]) => !existingBaseIDs.has(baseID))
    .map(([baseID, draft]) =>
      toMergedProductVariant({
        productVariant: { id: '', variantID, baseID, price: null, compareAtPrice: null },
        draft,
      }),
    );

  return [...updated, ...added];
};
